// Simple Web Audio preview of the interpreted MIDI: per-track oscillators approximating SID
// waveforms with a basic envelope, plus synthetic drums. For judging arrangement and timing,
// not chip-accurate sound.

import type { BendEvent, DrumKind, Interpretation, NoteEvent } from '../core/types';

const LOOKAHEAD = 0.25;
const INTERVAL_MS = 30;

function midiHz(note: number): number {
  return 440 * Math.pow(2, (note - 69) / 12);
}

export class PreviewPlayer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private trackGains: GainNode[] = [];
  private noise: AudioBuffer | null = null;
  private song: Interpretation | null = null;
  private timer: number | null = null;
  private ctxStart = 0;
  private songStart = 0;
  private cursor = 0;
  private nextNote = 0;
  private active = new Set<AudioScheduledSourceNode>();
  private mutes: boolean[] = [];
  private solos: boolean[] = [];
  private bendsByChannel = new Map<number, BendEvent[]>();
  loop = false;
  volume = 0.6;
  onEnded: (() => void) | null = null;

  get playing(): boolean {
    return this.timer !== null;
  }

  /** Current song position in seconds. */
  get position(): number {
    if (!this.ctx || !this.playing) return this.songStart;
    const pos = this.songStart + (this.ctx.currentTime - this.ctxStart);
    return this.song ? Math.min(pos, this.song.duration) : pos;
  }

  load(song: Interpretation) {
    const wasPlaying = this.playing;
    const pos = this.position;
    if (wasPlaying) this.stopVoices();
    this.song = song;
    this.bendsByChannel.clear();
    for (const b of song.bends) {
      const list = this.bendsByChannel.get(b.channel) ?? [];
      list.push(b);
      this.bendsByChannel.set(b.channel, list);
    }
    this.mutes.length = song.tracks.length;
    this.solos.length = song.tracks.length;
    if (this.ctx) this.buildGraph();
    if (wasPlaying) this.play(Math.min(pos, song.duration));
    else this.songStart = Math.min(this.songStart, song.duration);
  }

  setTrackState(mutes: boolean[], solos: boolean[]) {
    this.mutes = mutes.slice();
    this.solos = solos.slice();
    this.applyGains();
  }

  async play(from = this.songStart) {
    if (!this.song) return;
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.buildGraph();
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.stopVoices();
    this.songStart = from >= this.song.duration ? 0 : from;
    this.ctxStart = this.ctx.currentTime + 0.05;
    this.cursor = this.songStart;
    this.nextNote = this.firstNoteAt(this.songStart);
    this.timer = window.setInterval(() => this.schedule(), INTERVAL_MS);
    this.schedule();
  }

  stop() {
    const pos = this.position;
    this.stopVoices();
    this.songStart = pos;
  }

  seek(time: number) {
    if (this.playing) void this.play(time);
    else this.songStart = Math.max(0, time);
  }

  dispose() {
    this.stopVoices();
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.trackGains = [];
  }

  private stopVoices() {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    for (const s of this.active) {
      try {
        s.stop();
      } catch {
        /* already stopped */
      }
    }
    this.active.clear();
  }

  private buildGraph() {
    const ctx = this.ctx!;
    if (!this.master) {
      const comp = ctx.createDynamicsCompressor();
      comp.connect(ctx.destination);
      this.master = ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(comp);
      const n = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const d = n.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      this.noise = n;
    }
    for (const g of this.trackGains) g.disconnect();
    this.trackGains = (this.song?.tracks ?? []).map(() => {
      const g = ctx.createGain();
      g.connect(this.master!);
      return g;
    });
    this.applyGains();
  }

  private applyGains() {
    const anySolo = this.solos.some(Boolean);
    this.trackGains.forEach((g, i) => {
      const audible = anySolo ? !!this.solos[i] : !this.mutes[i];
      g.gain.setTargetAtTime(audible ? 0.22 : 0, this.ctx!.currentTime, 0.01);
    });
  }

  private firstNoteAt(time: number): number {
    const notes = this.song!.notes;
    let lo = 0;
    let hi = notes.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (notes[mid].start < time) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  private schedule() {
    const ctx = this.ctx!;
    const song = this.song!;
    const songNow = this.songStart + (ctx.currentTime - this.ctxStart);
    const horizon = songNow + LOOKAHEAD;
    while (this.nextNote < song.notes.length && song.notes[this.nextNote].start < horizon) {
      const n = song.notes[this.nextNote++];
      if (n.start >= this.cursor - 0.01) this.playNote(n);
    }
    this.cursor = horizon;
    if (songNow >= song.duration) {
      if (this.loop) {
        void this.play(0);
      } else {
        this.stopVoices();
        this.songStart = 0;
        this.onEnded?.();
      }
    }
  }

  private toCtx(songTime: number): number {
    return this.ctxStart + (songTime - this.songStart);
  }

  private track(n: NoteEvent): AudioNode {
    return this.trackGains[n.track] ?? this.master!;
  }

  private playNote(n: NoteEvent) {
    if (n.drum) {
      this.playDrum(n.drum, n);
      return;
    }
    const ctx = this.ctx!;
    const t0 = Math.max(ctx.currentTime, this.toCtx(n.start));
    const t1 = Math.max(t0 + 0.02, this.toCtx(n.end));
    const vel = n.velocity / 127;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(vel, t0 + 0.004);
    env.gain.setTargetAtTime(vel * 0.7, t0 + 0.004, 0.08);
    env.gain.setTargetAtTime(0, t1, 0.03);
    env.connect(this.track(n));

    let src: AudioScheduledSourceNode;
    const w = n.waveform;
    if (w & 8 && !(w & 7)) {
      const ns = ctx.createBufferSource();
      ns.buffer = this.noise;
      ns.loop = true;
      ns.playbackRate.value = Math.min(4, Math.max(0.1, midiHz(n.note) / 2000));
      src = ns;
    } else {
      const osc = ctx.createOscillator();
      osc.type = w & 4 ? 'square' : w & 2 ? 'sawtooth' : w & 1 ? 'triangle' : 'square';
      osc.frequency.value = midiHz(n.note);
      const bends = this.bendsByChannel.get(n.channel);
      if (bends && this.song) {
        const range = this.song.bendRange * 100;
        const before = bends.filter((b) => b.time <= n.start).at(-1);
        osc.detune.setValueAtTime(before ? before.value * range : 0, t0);
        for (const b of bends) {
          if (b.time > n.start && b.time < n.end)
            osc.detune.setValueAtTime(b.value * range, this.toCtx(b.time));
        }
      }
      src = osc;
    }
    src.connect(env);
    src.start(t0);
    src.stop(t1 + 0.2);
    this.active.add(src);
    src.onended = () => this.active.delete(src);
  }

  private playDrum(kind: DrumKind, n: NoteEvent) {
    const ctx = this.ctx!;
    const t0 = Math.max(ctx.currentTime, this.toCtx(n.start));
    const vel = n.velocity / 127;
    const env = ctx.createGain();
    env.connect(this.track(n));
    const decay =
      kind === 'kick'
        ? 0.18
        : kind === 'snare'
          ? 0.15
          : kind === 'closedHat'
            ? 0.04
            : kind === 'openHat'
              ? 0.3
              : 0.2;
    env.gain.setValueAtTime(vel * 1.2, t0);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + decay);
    let src: AudioScheduledSourceNode;
    if (kind === 'kick' || kind === 'tom') {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      const f0 = kind === 'kick' ? 140 : 220;
      osc.frequency.setValueAtTime(f0, t0);
      osc.frequency.exponentialRampToValueAtTime(f0 / 3, t0 + decay);
      osc.connect(env);
      src = osc;
    } else {
      const ns = ctx.createBufferSource();
      ns.buffer = this.noise;
      const filter = ctx.createBiquadFilter();
      filter.type = kind === 'snare' ? 'bandpass' : 'highpass';
      filter.frequency.value = kind === 'snare' ? 1800 : 7000;
      ns.connect(filter);
      filter.connect(env);
      src = ns;
    }
    src.start(t0);
    src.stop(t0 + decay + 0.05);
    this.active.add(src);
    src.onended = () => this.active.delete(src);
  }
}
